import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { FileText, Plus, Trash2, Sparkles, ChevronDown, ChevronRight, Pencil, Eye, Copy, Upload, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { contractTemplatesAPI } from '../lib/api';
import ContractDocument from '../components/ContractDocument';

const ContractTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => { reload(); }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await contractTemplatesAPI.list();
      setTemplates(res.data || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async (force = false) => {
    if (force && !window.confirm('Refresh the 2nd-year template body and fields with the current code defaults? This will NOT touch any contracts that are already signed.')) return;
    try {
      const res = await contractTemplatesAPI.seedDefaults(force);
      toast.success(res.data?.message || 'Seeded');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seed failed');
    }
  };

  const handleClone = async (t) => {
    try {
      await contractTemplatesAPI.clone(t.id);
      toast.success(`Cloned "${t.name}"`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clone failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    try {
      await contractTemplatesAPI.delete(id);
      toast.success('Template deleted');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const grouped = templates.reduce((acc, t) => {
    const k = t.category;
    if (!acc[k]) acc[k] = [];
    acc[k].push(t);
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="contract-templates-page" className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-ica-navy">Contract Templates</h1>
            <p className="text-gray-500 mt-1">Master templates for the Experiential Training Agreement. Clause text is locked; only field labels & sources are editable.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              data-testid="seed-defaults-btn"
              variant="outline"
              onClick={() => handleSeed(false)}
             
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Seed 2nd-Year Default
            </Button>
            {templates.some(t => t.year_of_study === '2nd Year' && t.category === 'establishment') && (
              <Button
                data-testid="reseed-defaults-btn"
                variant="ghost"
                onClick={() => handleSeed(true)}
                className="text-gray-500 hover:text-ica-navy"
                title="Refresh body & fields of the existing 2nd-year template from code defaults"
              >
                <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh seed
              </Button>
            )}
            <Button
              data-testid="import-docx-btn"
              variant="outline"
              onClick={() => setImportOpen(true)}
             
            >
              <Upload className="w-4 h-4 mr-1.5" /> Import .docx
            </Button>
            <Button
              data-testid="new-template-btn"
              onClick={() => { setEditing(null); setEditorOpen(true); }}
              
            >
              <Plus className="w-4 h-4 mr-1.5" /> New Template
            </Button>
          </div>
        </div>

        {Object.keys(grouped).length === 0 && (
          <Card>
            <CardContent className="py-16 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 mb-1">No contract templates yet.</p>
              <p className="text-sm text-gray-500 mb-4">Click <strong>Seed 2nd-Year Default</strong> to load the ICA Experiential Training Agreement.</p>
            </CardContent>
          </Card>
        )}

        {Object.entries(grouped).map(([category, list]) => (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-ica-navy">
                <FileText className="w-5 h-5" />
                {category === 'establishment' ? 'Establishment Agreements (one per restaurant, perpetuity)' : 'Placement Agreements (per-student)'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {list.map((t) => (
                  <div
                    key={t.id}
                    data-testid={`template-row-${t.id}`}
                    className="border rounded-lg px-4 py-3 hover:bg-gray-50 flex items-center justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{t.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {t.year_of_study} · {t.fields.length} field{t.fields.length === 1 ? '' : 's'}
                        {!t.active && <span className="ml-2 text-amber-600">(inactive)</span>}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button data-testid={`preview-${t.id}`} variant="ghost" size="sm" onClick={() => setPreviewOpen(t)}>
                        <Eye className="w-4 h-4 mr-1" /> Preview
                      </Button>
                      <Button data-testid={`edit-${t.id}`} variant="outline" size="sm" onClick={() => { setEditing(t); setEditorOpen(true); }}>
                        <Pencil className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button data-testid={`clone-${t.id}`} variant="ghost" size="sm" onClick={() => handleClone(t)} title="Duplicate this template (e.g. for another year)">
                        <Copy className="w-4 h-4 mr-1" /> Clone
                      </Button>
                      <Button data-testid={`delete-${t.id}`} variant="ghost" size="sm" onClick={() => handleDelete(t.id)} className="text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <TemplateEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        template={editing}
        onSaved={reload}
      />

      <ImportDocxDialog open={importOpen} onOpenChange={setImportOpen} onImported={reload} />

      <Dialog open={!!previewOpen} onOpenChange={(o) => !o && setPreviewOpen(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewOpen?.name}</DialogTitle>
            <DialogDescription>Preview with empty placeholders</DialogDescription>
          </DialogHeader>
          {previewOpen && (
            <ContractDocument bodyHtml={previewOpen.body_html} fieldValues={{}} />
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

// ---- Template editor ----
const TemplateEditor = ({ open, onOpenChange, template, onSaved }) => {
  const isEdit = !!template;
  const [form, setForm] = useState(empty());
  const [expandedField, setExpandedField] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (template) {
      setForm({
        category: template.category || 'establishment',
        year_of_study: template.year_of_study || '2nd Year',
        name: template.name || '',
        description: template.description || '',
        body_html: template.body_html || '',
        fields: (template.fields || []).map((f) => ({ ...f })),
        signer_role_label: template.signer_role_label || 'Industry Partner Signatory',
        witness_role_label: template.witness_role_label || 'Industry Partner Witness',
        ica_role_label: template.ica_role_label || 'Institute of Culinary Arts',
        active: template.active !== false,
        logo_url: template.logo_url || '',
        sender_block_html: template.sender_block_html || '',
      });
    } else {
      setForm(empty());
    }
  }, [open, template]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.body_html.trim()) { toast.error('Body HTML is required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await contractTemplatesAPI.update(template.id, form);
      } else {
        await contractTemplatesAPI.create(form);
      }
      toast.success(isEdit ? 'Template updated' : 'Template created');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addField = () => {
    setForm({ ...form, fields: [...form.fields, { key: '', label: '', source: 'restaurant_fills', auto_from: '', default_value: '', type: 'text', required: false }] });
    setExpandedField(form.fields.length);
  };

  const updateField = (idx, patch) => {
    const next = form.fields.slice();
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, fields: next });
  };

  const removeField = (idx) => {
    setForm({ ...form, fields: form.fields.filter((_, i) => i !== idx) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="template-editor" className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Template' : 'New Contract Template'}</DialogTitle>
          <DialogDescription>Manage template metadata, body HTML, and field registry.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="tpl-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="establishment">Establishment (perpetuity)</SelectItem>
                  <SelectItem value="placement">Placement (per student)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Year of Study *</Label>
              <Select value={form.year_of_study} onValueChange={(v) => setForm({ ...form, year_of_study: v })}>
                <SelectTrigger data-testid="tpl-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1st Year">1st Year</SelectItem>
                  <SelectItem value="2nd Year">2nd Year</SelectItem>
                  <SelectItem value="3rd Year">3rd Year</SelectItem>
                  <SelectItem value="All">All years</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end pb-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <Checkbox data-testid="tpl-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} />
                Active
              </label>
            </div>
          </div>
          <div>
            <Label>Template Name *</Label>
            <Input data-testid="tpl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Description</Label>
            <Input data-testid="tpl-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>

          {/* Per-template branding (overrides global Settings) */}
          <div className="border rounded-lg p-3 bg-gray-50 space-y-3">
            <p className="text-xs font-semibold text-ica-navy uppercase tracking-wide">
              Branding overrides for this template (optional — leave blank to inherit from Settings)
            </p>
            <div>
              <Label className="text-xs">Logo URL override</Label>
              <Input
                data-testid="tpl-logo-url"
                value={form.logo_url || ''}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="https://… or data:image/png;base64,…"
              />
              {form.logo_url && (
                <div className="mt-2 inline-block border bg-white rounded p-2">
                  <img src={form.logo_url} alt="Logo override" className="max-h-[60px] max-w-[200px]" />
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Footer / sender block (HTML)</Label>
              <p className="text-[11px] text-gray-500 mb-1">
                Appended after the signature area. Useful for the per-letter ICA contact lines (T:, E:, W:, F:) or any
                template-specific legal footer.
              </p>
              <Textarea
                data-testid="tpl-sender-block"
                rows={4}
                value={form.sender_block_html || ''}
                onChange={(e) => setForm({ ...form, sender_block_html: e.target.value })}
                className="font-mono text-xs"
                placeholder={'<p><strong>Antoinette Van Zyl</strong> · Industry Liaison &amp; Placement Co-ordinator</p>\n<p>T: +27 21 555 0100 · E: placements@icachef.co.za</p>'}
              />
            </div>
          </div>

          <div>
            <Label>Body HTML *</Label>
            <p className="text-xs text-gray-500 mb-1">Use <code>{'{{field_key}}'}</code> placeholders for fields, and <code>{'<div class="sig-slot" data-slot="signer|witness|ica_signer"></div>'}</code> for signatures.</p>
            <Textarea data-testid="tpl-body" rows={10} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} className="font-mono text-xs" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base">Field Registry ({form.fields.length})</Label>
              <Button type="button" size="sm" variant="outline" onClick={addField} data-testid="add-field-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Field
              </Button>
            </div>
            <div className="space-y-2 border rounded-lg p-2 bg-gray-50">
              {form.fields.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">No fields yet.</p>
              )}
              {form.fields.map((f, idx) => {
                const expanded = expandedField === idx;
                return (
                  <div key={idx} className="bg-white border rounded">
                    <button
                      type="button"
                      onClick={() => setExpandedField(expanded ? null : idx)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
                    >
                      {expanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                      <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{f.key || '(no key)'}</code>
                      <span className="text-sm">{f.label || '(no label)'}</span>
                      <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${f.source === 'auto' ? 'bg-emerald-100 text-emerald-700' : f.source === 'signatory_fills' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                        {f.source}
                      </span>
                    </button>
                    {expanded && (
                      <div className="border-t p-3 space-y-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label>Key *</Label>
                          <Input data-testid={`field-key-${idx}`} value={f.key} onChange={(e) => updateField(idx, { key: e.target.value })} />
                        </div>
                        <div>
                          <Label>Label *</Label>
                          <Input data-testid={`field-label-${idx}`} value={f.label} onChange={(e) => updateField(idx, { label: e.target.value })} />
                        </div>
                        <div>
                          <Label>Source *</Label>
                          <Select value={f.source} onValueChange={(v) => updateField(idx, { source: v })}>
                            <SelectTrigger data-testid={`field-source-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="auto">Auto (from system)</SelectItem>
                              <SelectItem value="restaurant_fills">Restaurant fills</SelectItem>
                              <SelectItem value="signatory_fills">Signatory fills (on signing page)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Auto From</Label>
                          <Input
                            data-testid={`field-autofrom-${idx}`}
                            placeholder="e.g. restaurant.name"
                            value={f.auto_from || ''}
                            onChange={(e) => updateField(idx, { auto_from: e.target.value })}
                          />
                          <p className="text-[10px] text-gray-500 mt-1">restaurant.*, settings.*, today.day_ordinal, today.month_year</p>
                        </div>
                        <div>
                          <Label>Default value</Label>
                          <Input value={f.default_value || ''} onChange={(e) => updateField(idx, { default_value: e.target.value })} />
                        </div>
                        <div className="flex items-end gap-3">
                          <label className="inline-flex items-center gap-2 text-sm">
                            <Checkbox checked={!!f.required} onCheckedChange={(v) => updateField(idx, { required: !!v })} />
                            Required
                          </label>
                          <Button variant="ghost" size="sm" onClick={() => removeField(idx)} className="text-red-600 hover:bg-red-50 ml-auto">
                            <Trash2 className="w-4 h-4 mr-1" /> Remove
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="save-template-btn" onClick={handleSave} disabled={saving} >
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const empty = () => ({
  category: 'establishment',
  year_of_study: '2nd Year',
  name: '',
  description: '',
  body_html: '<div class="contract-document"><h1 class="contract-title">New Contract</h1><p>Edit this template body…</p><div class="sig-slot" data-slot="signer"></div></div>',
  fields: [],
  signer_role_label: 'Industry Partner Signatory',
  witness_role_label: 'Industry Partner Witness',
  ica_role_label: 'Institute of Culinary Arts',
  active: true,
  logo_url: '',
  sender_block_html: '',
});

// ---- Import .docx Dialog ----
const ImportDocxDialog = ({ open, onOpenChange, onImported }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('establishment');
  const [year, setYear] = useState('1st Year');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) { setFile(null); setName(''); setCategory('establishment'); setYear('1st Year'); }
  }, [open]);

  const handleImport = async () => {
    if (!file) { toast.error('Choose a .docx file'); return; }
    setImporting(true);
    try {
      const res = await contractTemplatesAPI.importDocx(file, { name, category, year_of_study: year });
      const warnings = res.data?.warnings || [];
      toast.success(`Template imported${warnings.length ? ` (${warnings.length} formatting note${warnings.length === 1 ? '' : 's'} — review the preview)` : ''}`);
      onImported?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="import-docx-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import contract from Word (.docx)</DialogTitle>
          <DialogDescription>
            Upload a .docx file with the contract clauses. The ICA logo header, a Signatory + Witness signature area, and the standard field registry are added automatically. Review the preview and tweak fields in the editor afterwards.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Word file (.docx) *</Label>
            <input
              data-testid="docx-file-input"
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm border border-gray-300 rounded p-2"
            />
            {file && <p className="text-xs text-gray-500 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category *</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="docx-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="establishment">Establishment (perpetuity)</SelectItem>
                  <SelectItem value="placement">Placement (per student)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Year *</Label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger data-testid="docx-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1st Year">1st Year</SelectItem>
                  <SelectItem value="2nd Year">2nd Year</SelectItem>
                  <SelectItem value="3rd Year">3rd Year</SelectItem>
                  <SelectItem value="All">All / Specialist</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Template name</Label>
            <Input
              data-testid="docx-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. ICA Experiential Training Agreement (1st Year)"
            />
            <p className="text-[11px] text-gray-500 mt-1">Optional — defaults to the file name.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="confirm-import-btn" disabled={!file || importing} onClick={handleImport} >
            {importing ? 'Importing…' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContractTemplates;
